/* V154 — Variant B copy + Live AI Environment.
   Replaces the old "Máte e-shop?" intro with concrete loss-aversion
   numbers (€3 200, €1 200, €89). Portrait dimmed; foreground filled
   with animated charts, scan lines, HUD frames, particle data nodes. */

import { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./AdAgentVideo.css";
import { AD_TRANSLATIONS, AD_LANGUAGES, getAdLang } from "./AdAgentVideo.i18n";
import { useAdRecorder } from "./useAdRecorder";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PORTRAIT_URL =
  "https://customer-assets.emergentagent.com/job_b798ee84-c0e9-45cd-8132-8a7a658b0230/artifacts/8hjkbb04_Screenshot_20260523_153429_Photos.jpg";

const AGENT_VISUALS = [
  { emoji: "📧", accent: "#3B82F6", price: 99 },
  { emoji: "🛒", accent: "#F59E0B", price: 89 },
  { emoji: "🎯", accent: "#10B981", price: 99 },
];

/* ─── Live AI Background — pure-CSS animated dashboard noise ─────── */
function LiveAIBackground() {
  return (
    <div className="ai-bg-fx" data-testid="ai-bg-fx">
      {/* Animated grid */}
      <div className="ai-grid" />

      {/* Floating chart card #1 — bar chart */}
      <div className="ai-card ai-card-1">
        <div className="ai-card-head">
          <span className="ai-card-dot" /> ROI · 12 mes
        </div>
        <div className="ai-bars">
          {[35, 55, 42, 78, 88, 95, 70].map((h, i) => (
            <div key={i} className="ai-bar" style={{ "--h": `${h}%`, animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
      </div>

      {/* Floating chart card #2 — KPI counter */}
      <div className="ai-card ai-card-2">
        <div className="ai-card-head">
          <span className="ai-card-dot green" /> RECOVERY
        </div>
        <div className="ai-kpi">
          <span className="ai-kpi-num">+€3 200</span>
          <span className="ai-kpi-trend">▲ 31%</span>
        </div>
        <div className="ai-line">
          <svg viewBox="0 0 100 30" preserveAspectRatio="none">
            <polyline className="ai-line-stroke"
                      points="0,25 15,20 30,22 45,15 60,18 75,8 90,10 100,5" />
            <polyline className="ai-line-stroke-trace"
                      points="0,25 15,20 30,22 45,15 60,18 75,8 90,10 100,5" />
          </svg>
        </div>
      </div>

      {/* Floating chart card #3 — pie / donut */}
      <div className="ai-card ai-card-3">
        <div className="ai-card-head">
          <span className="ai-card-dot amber" /> AUTOMATION
        </div>
        <div className="ai-donut">
          <svg viewBox="0 0 42 42">
            <circle cx="21" cy="21" r="15.9" className="ai-donut-bg" />
            <circle cx="21" cy="21" r="15.9" className="ai-donut-fg" />
          </svg>
          <div className="ai-donut-label">93%</div>
        </div>
      </div>

      {/* Floating chart card #4 — log stream */}
      <div className="ai-card ai-card-4">
        <div className="ai-card-head">
          <span className="ai-card-dot blue" /> AGENT LOG
        </div>
        <div className="ai-log">
          <div className="ai-log-row">→ helpdesk: reply sent</div>
          <div className="ai-log-row">→ cart: recovered €127</div>
          <div className="ai-log-row">→ scan: 12 leads found</div>
          <div className="ai-log-row">→ sales: gap detected</div>
        </div>
      </div>

      {/* Scan line sweeping across */}
      <div className="ai-scanline" />

      {/* Data particles */}
      {Array.from({ length: 14 }).map((_, i) => (
        <span
          key={i}
          className="ai-particle"
          style={{
            "--x": `${(i * 73) % 100}%`,
            "--y": `${(i * 53) % 100}%`,
            "--delay": `${i * 0.4}s`,
            "--dur": `${4 + (i % 4)}s`,
          }}
        />
      ))}

      {/* HUD corners (top-left, top-right, bottom-left, bottom-right) */}
      <div className="ai-hud ai-hud-tl" />
      <div className="ai-hud ai-hud-tr" />
      <div className="ai-hud ai-hud-bl" />
      <div className="ai-hud ai-hud-br" />
    </div>
  );
}

const QUEUE_VARIANTS = [];
for (const lang of ["sk", "cs", "en", "de", "pl", "hu"]) {
  for (const aspect of ["9-16", "1-1", "16-9"]) {
    QUEUE_VARIANTS.push({ lang, aspect });
  }
}

export default function AdAgentVideo() {
  const [aspect, setAspect] = useState("9-16");
  const [showHelp, setShowHelp] = useState(false);
  const [lang, setLang] = useState("sk");
  const recorder = useAdRecorder();

  // Batch queue state
  const [queueMode, setQueueMode] = useState(false);
  const [queueIndex, setQueueIndex] = useState(0);
  const [queueActive, setQueueActive] = useState(false);

  useEffect(() => {
    setLang(getAdLang());
    let queueDetected = false;
    let aspectDetected = "9-16";
    let langDetected = getAdLang();
    // Detect queue mode from ?queue=1
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("queue") === "1") { setQueueMode(true); queueDetected = true; }
      // Optional ?aspect= override
      const a = sp.get("aspect");
      if (a && ["9-16", "1-1", "16-9"].includes(a)) { setAspect(a); aspectDetected = a; }
    }
    // Fire pageview (fire-and-forget, ignore errors)
    try {
      axios.post(`${API}/agents/ad-pageview`, {
        lang: langDetected,
        aspect: aspectDetected,
        queue: queueDetected,
        record_started: false,
        referrer: typeof document !== "undefined" ? (document.referrer || "") : "",
      }).catch(() => {});
    } catch { /* noop */ }
  }, []);

  const setLanguage = (code) => {
    setLang(code);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", code);
    window.history.replaceState({}, "", url.toString());
  };

  const handleRecord = () => {
    if (recorder.recording) {
      recorder.stop();
    } else {
      recorder.start({ filenameBase: `toprankpulse-ad-${lang}-${aspect}` });
      // Track record-started event (fire-and-forget)
      try {
        axios.post(`${API}/agents/ad-pageview`, {
          lang, aspect, queue: queueMode, record_started: true,
          referrer: typeof document !== "undefined" ? (document.referrer || "") : "",
        }).catch(() => {});
      } catch { /* noop */ }
    }
  };

  // Batch recorder — sequentially records all 18 variants from a single
  // screen-capture permission grant. Total time: ~18 × 44s ≈ 14 min.
  // We trigger ONE recorder.start() from the user click so the browser grants
  // permission. From then on, useAdRecorder reuses the same stream for every
  // subsequent variant in the queue (no further prompts).
  const startBatchQueue = async () => {
    setQueueActive(true);
    setQueueIndex(0);
    // Kick off the first variant immediately from the user gesture so the
    // browser's "user activation" is consumed by getDisplayMedia().
    const v = QUEUE_VARIANTS[0];
    setLang(v.lang);
    setAspect(v.aspect);
    // Allow a tick for state to apply, then start the first recording
    setTimeout(() => {
      recorder.start({
        filenameBase: `toprankpulse-ad-${v.lang}-${v.aspect}`,
      });
    }, 400);
  };

  // When queue finishes (index passes the end), release the screen-capture
  // stream so the browser's "Sharing this tab" banner disappears.
  useEffect(() => {
    if (!queueMode || !queueActive) return;
    if (queueIndex >= QUEUE_VARIANTS.length) {
      setQueueActive(false);
      if (recorder.endSession) recorder.endSession();
      return;
    }
    // Skip index=0 — already kicked off from the user click in startBatchQueue
    if (queueIndex === 0) return;
    const v = QUEUE_VARIANTS[queueIndex];
    setLang(v.lang);
    setAspect(v.aspect);
    // Wait 800ms for animation to restart after lang/aspect change,
    // then trigger record. Stream is already granted from the first click.
    const t = setTimeout(() => {
      recorder.start({
        filenameBase: `toprankpulse-ad-${v.lang}-${v.aspect}`,
      });
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueActive, queueIndex, queueMode]);

  // When the current recording finishes, advance to the next variant
  const prevRecordingRef = useRef(false);
  useEffect(() => {
    if (!queueActive) return;
    // recorder.recording transitions true → false on stop
    if (prevRecordingRef.current && !recorder.recording) {
      // Recording for this variant just finished — advance after a short
      // breathing room so the download dialog can fire.
      const t = setTimeout(() => {
        setQueueIndex((i) => i + 1);
      }, 1500);
      prevRecordingRef.current = recorder.recording;
      return () => clearTimeout(t);
    }
    prevRecordingRef.current = recorder.recording;
  }, [recorder.recording, queueActive]);

  const t = AD_TRANSLATIONS[lang] || AD_TRANSLATIONS.sk;

  return (
    <div className="ad-page" data-testid="ad-page">
      <h1 data-testid="ad-page-title">{t.page_title}</h1>
      <p data-testid="ad-page-sub">{t.page_sub}</p>

      <div className="ad-toolbar" data-testid="ad-lang-toolbar">
        {AD_LANGUAGES.map((l) => (
          <button
            key={l.code}
            className={lang === l.code ? "active" : ""}
            onClick={() => setLanguage(l.code)}
            data-testid={`ad-lang-${l.code}`}
            title={l.name}
          >
            {l.flag} {l.code.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="ad-toolbar" data-testid="ad-toolbar">
        {["9-16", "1-1", "16-9"].map((r) => (
          <button
            key={r}
            className={aspect === r ? "active" : ""}
            onClick={() => setAspect(r)}
            data-testid={`ad-aspect-${r}`}
          >
            {r === "9-16" && t.aspect_9_16}
            {r === "1-1" && t.aspect_1_1}
            {r === "16-9" && t.aspect_16_9}
          </button>
        ))}
        <button onClick={() => setShowHelp((s) => !s)} data-testid="ad-toggle-help">
          {showHelp ? t.hide_tip : t.show_tip}
        </button>
      </div>

      <div className="ad-record-wrap" data-testid="ad-record-wrap">
        {queueMode && (
          <div className="ad-queue-banner" data-testid="ad-queue-banner">
            <div className="ad-queue-title">
              🎬 Batch Record Mode · 18 variantov
            </div>
            {queueActive ? (
              <>
                <div className="ad-queue-progress">
                  <span className="ad-queue-num">
                    {queueIndex + 1} / {QUEUE_VARIANTS.length}
                  </span>{" "}
                  · {QUEUE_VARIANTS[queueIndex]?.lang.toUpperCase()} ·{" "}
                  {QUEUE_VARIANTS[queueIndex]?.aspect}
                </div>
                <div className="ad-queue-bar">
                  <div
                    className="ad-queue-bar-fill"
                    style={{ width: `${(queueIndex / QUEUE_VARIANTS.length) * 100}%` }}
                  />
                </div>
                <p className="ad-record-hint">
                  Po dokončení každého variantu sa stiahne .webm súbor a automaticky
                  začne ďalší. <strong>Nezatváraj tab</strong> kým neuvidíš 18/18.
                </p>
              </>
            ) : (
              <>
                <p className="ad-record-hint">
                  Klikni tlačidlo nižšie. Browser sa raz spýta na permission, potom sa
                  všetkých 18 variantov nahrá automaticky (~14 min spolu).
                </p>
                <button
                  className="ad-record-btn"
                  onClick={startBatchQueue}
                  data-testid="ad-batch-start"
                >
                  ▶ START BATCH (18 × 44s ≈ 14 min)
                </button>
              </>
            )}
          </div>
        )}
        {!queueMode && (
          <>
            <button
              className={`ad-record-btn ${recorder.recording ? "recording" : ""}`}
              onClick={handleRecord}
              data-testid="ad-record-btn"
            >
              {recorder.recording ? t.rec_btn_stop : t.rec_btn}
            </button>
            {!recorder.recording && (
              <p className="ad-record-hint" data-testid="ad-record-hint">
                {t.rec_hint}
              </p>
            )}
          </>
        )}
        {recorder.recording && (
          <div className="ad-record-progress" data-testid="ad-record-progress">
            <div
              className="ad-record-progress-fill"
              style={{ width: `${recorder.progress}%` }}
            />
            <span className="ad-record-progress-label">
              {t.rec_progress} · {Math.round(recorder.progress)}%
            </span>
          </div>
        )}
        {recorder.error && (
          <p className="ad-record-error" data-testid="ad-record-error">
            {t.rec_err} {recorder.error}
          </p>
        )}
      </div>

      {showHelp && (
        <div className="ad-help" data-testid="ad-help">
          <strong>{t.help_title}</strong>
          <br />
          1. <strong>{t.help_mac}</strong> <code>Shift+Cmd+5</code> {t.help_mac_steps}
          <br />
          2. <strong>{t.help_win}</strong> <code>Win+G</code> {t.help_win_steps}
          <br />
          3. <strong>{t.help_online}</strong> {t.help_online_steps}
          <br />
          4. {t.help_stop} <strong>{t.help_one_loop}</strong> {t.help_or_longer}
          <br />
          5. {t.help_done}
        </div>
      )}

      <div
        className={`ad-stage aspect-${aspect} variant-b`}
        data-testid="ad-stage"
        key={`${aspect}-${lang}`}
      >
        {/* Portrait dimmed in the back */}
        <div
          className="ad-bg dimmed"
          style={{ backgroundImage: `url(${PORTRAIT_URL})` }}
          data-testid="ad-bg"
        />
        <div className="ad-vignette" />

        {/* Live AI environment (charts, particles, scan lines) */}
        <LiveAIBackground />

        <div className="ad-grain" />

        {/* Slide 1 — €3 200 lost in carts */}
        <div className="ad-slide slide-1">
          <p className="ad-eyebrow">{t.eyebrow_intro}</p>
          <p className="ad-h1 red">{t.slide_1}</p>
          <p className="ad-sub">{t.slide_1_sub}</p>
        </div>

        {/* Slide 2 — €1 200 spent on inbox */}
        <div className="ad-slide slide-2">
          <p className="ad-eyebrow">{t.eyebrow_pain}</p>
          <p className="ad-h1 amber">{t.slide_2}</p>
          <p className="ad-sub">{t.slide_2_sub}</p>
        </div>

        {/* Slide 3 — €89 is the fix */}
        <div className="ad-slide slide-3">
          <p className="ad-eyebrow">{t.eyebrow_solution}</p>
          <p className="ad-h1 lime">
            {t.slide_3_a} <br />
            <span className="accent">{t.slide_3_b}</span>
          </p>
          <p className="ad-sub">{t.slide_3_sub}</p>
        </div>

        {/* Slides 4-6 — Agents */}
        {t.agents.map((a, i) => {
          const v = AGENT_VISUALS[i];
          return (
            <div className={`ad-slide slide-${i + 4}`} key={`${lang}-${a.name}`}>
              <div className="agent-card" style={{ borderColor: v.accent }}>
                <div className="price-tag">€{v.price}{t.price_suffix}</div>
                <span className="agent-emoji">{v.emoji}</span>
                <div className="agent-name" style={{ color: v.accent }}>
                  {a.name}
                </div>
                <div className="agent-tagline">{a.tagline}</div>
                <div className="agent-saves">
                  {a.saves.map((s) => (
                    <div className="save-row" key={s.label}>
                      <span className="num">{s.num}</span> {s.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}

        {/* Slide 7 — outro CTA */}
        <div className="ad-slide slide-7">
          <div className="outro">
            <p className="brand">{t.cta_brand}</p>
            <button className="cta-btn">{t.cta_main}</button>
            <p className="url">{t.cta_url}</p>
            <p className="fine-print">{t.cta_fine}</p>
          </div>
        </div>

        <div className="ad-progress">
          <div className="ad-progress-fill" />
        </div>
      </div>
    </div>
  );
}
